import { useEffect, useState } from "react";
import { Helmet } from 'react-helmet-async';
import { aboutUsService, formatAboutUsData } from "../services/aboutUsService";
import {
  clientFeedbackService,
  formatClientFeedbackData,
} from "../services/clientFeedbackService";
import { getHomeData } from "../services/homeService";
import OverviewSection from "../components/HomePage/OverviewSection";
import OurImpactSection from "../components/HomePage/OurImpactSection";
import HonorsInspire from "../components/HomePage/HonorsInspire";
import ProductSection from "../components/HomePage/ProductSection";
import CaseStudiesSection from "../components/Common/CaseStudiesSection";
import ServicesSection from "../components/HomePage/ServicesSection";
import ClientsPartnersSection from "../components/Common/ClientSection";
import RecentBlogPosts from "../components/Common/RecentBlogPosts";
import ScrollToTop from "../components/Common/ScrollToTop";
import { useBlogs } from "../hooks/useBlog";
import { initializeAnalytics } from "../utils/analytics";

const Home = () => {
  const [aboutUsData, setAboutUsData] = useState(null);
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { blogPosts, loading: blogLoading, error: blogError } = useBlogs();

  useEffect(() => {
    // Initialize analytics dynamically
    initializeAnalytics();

    const fetchData = async () => {
      try {
        // Fetch home data (including carousel and client images)
        const homeResponse = await getHomeData();
        console.log("🏠 Home data fetched:", homeResponse);
        setHomeData(homeResponse);

        // Fetch about us data
        const aboutResponse = await aboutUsService.getAboutUsData();
        if (aboutResponse.success && aboutResponse.data) {
          const formattedData = formatAboutUsData(aboutResponse.data);
          setAboutUsData(formattedData);
        }

        // Fetch client feedback data (if needed for testimonials elsewhere)
        const feedbackResponse =
          await clientFeedbackService.getClientFeedback();
        if (feedbackResponse.success && feedbackResponse.data) {
          const formattedTestimonials = formatClientFeedbackData(
            feedbackResponse.data
          );
          // Testimonials data available but not currently used in this component
          console.log("Client feedback loaded:", formattedTestimonials);
        }
      } catch (error) {
        console.error("Error fetching data for home page:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>Trusted eGovernance & Inclusive IT Partner | Business Automation</title>
        <meta 
          name="description" 
          content="Business Automation (BA) - Bangladesh's trusted eGovernance partner driving Smart Bangladesh. Explore inclusive careers and digital government innovation." 
        />
        <meta 
          name="keywords" 
          content="eGovernance Bangladesh, digital government solutions, ERP implementation Bangladesh, Smart Bangladesh technology, inclusive IT jobs, women in tech Bangladesh, trusted technology partner" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Trusted eGovernance & Inclusive IT Partner | Business Automation" />
        <meta property="og:description" content="Business Automation (BA)  Bangladesh's trusted eGovernance partner, driving Smart Bangladesh. Explore inclusive careers and digital government innovation." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/" />
        <meta name="twitter:title" content="Trusted eGovernance & Inclusive IT Partner | Business Automation" />
        <meta name="twitter:description" content="Business Automation (BA)  Bangladesh's trusted eGovernance partner, driving Smart Bangladesh. Explore inclusive careers and digital government innovation." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Structured Data (JSON-LD) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Business Automation Ltd.",
            "alternateName": "BA",
            "url": "https://www.ba-systems.com",
            "logo": "https://www.ba-systems.com/images/logo.png",
            "sameAs": [
              "https://www.linkedin.com/company/business-automation-ltd-bd",
              "https://www.facebook.com/BusinessAutomationLtd",
              "https://x.com/BusinessAutoBD"
            ],
            "description": "Business Automation Ltd. (BA) is Bangladesh's trusted eGovernance partner, delivering e-government solutions, digital transformation, and inclusive IT careers.",
            "foundingDate": "1998",
            "areaServed": "Bangladesh",
            "founder": "Shoeb Ahmed Masud",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "Customer Support",
              "email": "info@ba-systems.com",
              "telephone": "+8809602666777",
              "availableLanguage": ["en", "bn"]
            }
          })}
        </script>
      </Helmet>
      
      <OverviewSection />
      <OurImpactSection />
      {!loading && <HonorsInspire data={aboutUsData} />}
      <ServicesSection />
      <ProductSection />
      {!loading && (
        <ClientsPartnersSection
          showSeeMore={true}
          data={homeData?.clientSection || aboutUsData?.clientSection}
        />
      )}
      <CaseStudiesSection />
      <RecentBlogPosts
        blogPosts={blogPosts}
        title="Newsroom"
        loading={blogLoading}
        error={blogError}
      />
      <ScrollToTop />
    </>
  );
};

export default Home;
